import React, { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Auto "new version available" prompt.
 *
 * Detects new deploys via TWO independent paths:
 *  1. Service-worker `updatefound` event (fires when sw.js bytes change — bump
 *     CACHE_NAME in /public/sw.js for the SW to roll forward).
 *  2. Polls `GET /api/version` (server start time) every 60s. When the version
 *     differs from the value captured on mount, treats it as a fresh deploy.
 *     This catches deploys even when sw.js itself didn't change.
 *
 * The actual "skipWaiting" → reload flow is handled by `sw.js` on `activate`;
 * we just nudge the user.
 */
export default function NewVersionPrompt() {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const dismissedKey = 'bundle-version-dismissed-at';
    const refreshedKey = 'bundle-version-refreshed-at';
    // Persistent (survives reload) guard used to suppress the banner and dedupe
    // the controllerchange-triggered reload for a few minutes.
    const reloadingKey = 'bundle-version-reloading-at';
    const RELOAD_SUPPRESS_MS = 5 * 60_000; // 5 minutes

    // If we just triggered a reload, suppress the banner briefly on mount so
    // we don't end up in a "banner → refresh → controllerchange → reload →
    // banner → refresh → …" loop.
    const reloadingAt = parseInt(localStorage.getItem(reloadingKey) || '0', 10);
    const withinReloadGuard = reloadingAt && (Date.now() - reloadingAt < RELOAD_SUPPRESS_MS);

    const showPrompt = (worker = null) => {
      if (cancelled) return;
      // Persistent guard from a very recent reload
      if (Date.now() - (parseInt(localStorage.getItem(reloadingKey) || '0', 10)) < RELOAD_SUPPRESS_MS) return;
      // Suppress for 30s after a Refresh was tapped (session-local safety net)
      const refreshedAt = parseInt(sessionStorage.getItem(refreshedKey) || '0', 10);
      if (Date.now() - refreshedAt < 30_000) return;
      // Don't re-show within 5 minutes of dismissal
      const dismissedAt = parseInt(sessionStorage.getItem(dismissedKey) || '0', 10);
      if (Date.now() - dismissedAt < 5 * 60_000) return;
      if (worker) setWaitingWorker(worker);
      setShow(true);
    };

    // ── Path 1: service-worker update detection ───────────────────────────
    let swCleanup = () => {};
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration('/sw.js').then((registration) => {
        if (!registration || cancelled) return;
        if (registration.waiting) showPrompt(registration.waiting);
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              showPrompt(newWorker);
            }
          });
        });
        const id = setInterval(() => registration.update().catch(() => {}), 60_000);
        swCleanup = () => clearInterval(id);
      }).catch(() => {});

      let reloaded = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloaded) return;
        reloaded = true;
        // Respect the persistent guard — if we already reloaded within the
        // suppression window (e.g. iOS standalone firing controllerchange
        // repeatedly), do not reload again.
        const stampedAt = parseInt(localStorage.getItem(reloadingKey) || '0', 10);
        if (stampedAt && (Date.now() - stampedAt < RELOAD_SUPPRESS_MS)) return;
        // Stamp the guard BEFORE reload so the fresh page load sees it and
        // suppresses the banner instead of re-triggering the cycle.
        localStorage.setItem(reloadingKey, String(Date.now()));
        window.location.reload();
      });
    }

    // ── Path 2: backend deploy fingerprint (catches deploys even when SW
    //            didn't change) ─────────────────────────────────────────────
    let initialVersion = null;
    const checkVersion = async () => {
      try {
        const res = await fetch(`${API}/version`, { credentials: 'omit', cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (!data.version) return;
        if (initialVersion === null) {
          initialVersion = data.version;
        } else if (data.version !== initialVersion) {
          showPrompt();
        }
      } catch {
        /* network blip — ignore */
      }
    };
    // Only start polling once the reload-guard window has passed — otherwise
    // a stale server may still be reporting a "different" version right after
    // the reload and we'd immediately show the banner again.
    if (!withinReloadGuard) {
      checkVersion();
    }
    const versionPoll = setInterval(checkVersion, 60_000);

    return () => {
      cancelled = true;
      clearInterval(versionPoll);
      swCleanup();
    };
  }, []);

  const refreshNow = async () => {
    // Stamp "I just refreshed" so the banner won't re-fire on next mount
    sessionStorage.setItem('bundle-version-refreshed-at', String(Date.now()));
    // Hide immediately so the user sees the click took effect
    setShow(false);
    // Activate any waiting SW we know about. Plain reload() is not enough — a
    // SW stuck in "waiting" stays waiting across reloads and the banner just
    // re-fires on next mount. Sending SKIP_WAITING swaps controllers, which
    // our `controllerchange` listener turns into a clean one-shot reload.
    try {
      if (waitingWorker) {
        waitingWorker.postMessage({ type: 'SKIP_WAITING' });
        return;
      }
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration('/sw.js');
        if (reg && reg.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          return;
        }
      }
    } catch {
      /* fall through to hard reload */
    }
    // Version-poll path / no SW: hard reload
    window.location.reload();
  };

  const dismiss = () => {
    sessionStorage.setItem('bundle-version-dismissed-at', String(Date.now()));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed left-4 right-4 z-[80] mx-auto max-w-md"
      style={{ bottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}
      data-testid="new-version-prompt"
      role="dialog"
    >
      <div className="bg-bundle-green text-white rounded-2xl shadow-2xl border border-bundle-green-dark px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
          <RefreshCw className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold leading-tight">New version available</p>
          <p className="text-[11px] text-white/80">Tap refresh to load the latest features.</p>
        </div>
        <button
          type="button"
          onClick={refreshNow}
          data-testid="new-version-refresh-btn"
          className="px-3 py-1.5 bg-white text-bundle-green rounded-full text-xs font-bold hover:bg-bundle-cream transition-colors flex-shrink-0"
        >
          Refresh
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="new-version-dismiss-btn"
          className="text-white/60 hover:text-white p-1 -mr-1 flex-shrink-0"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
